/*
 * Copyright the original authors. Licensed under the Apache License, Version 2.0
 * (see LICENSE-APACHE-2.0.txt). Modified for trtMeet (see NOTICE).
 */
import { MeetPeerConnection } from './MeetPeerConnection.js';
import { MeetPeerServer } from './MeetPeerServer.js';

/** @typedef {import('./types.js').MeetPeerOptions} MeetPeerOptions */

export { MeetPeerServer } from './MeetPeerServer.js';
export { MeetPeerConnection } from './MeetPeerConnection.js';

/**
 * The peer client: serve a meeting (host) or connect to one (guest).
 * Configured by exactly three things, so nothing here knows where it runs:
 *
 * @param {Object} config
 * @param {() => string} config.signallerUrl ws(s):// URL of the signaller
 * @param {() => RTCIceServer[] | undefined} [config.iceServers] ICE servers (from the backend's /v1/ice)
 * @param {() => string | undefined} [config.token] the bearer credential sent in the signalling handshake
 *   (never in the URL): a CRM-issued JWT or a room host token for hosting, nothing for guests
 */
export function createMeetPeer({ signallerUrl, iceServers, token }) {
  const peerConfig = (options) => ({
    get authToken() {
      return token?.();
    },
    iceServers: options?.iceServers ?? iceServers?.(),
    signallerUrl: signallerUrl(),
    forceRelay: options?.forceRelay,
  });

  return {
    /**
     * Start serving; resolves once the signaller has issued an invite code.
     * @param {MeetPeerOptions} [options]
     * @returns {Promise<MeetPeerServer>}
     */
    async serve(options) {
      const server = new MeetPeerServer(peerConfig(options));
      await server.start(options);
      return server;
    },

    /**
     * Connect to a served meeting by invite code.
     * @param {string} invitecode
     * @param {MeetPeerOptions} [options]
     * @returns {Promise<MeetPeerConnection>}
     */
    async connect(invitecode, options) {
      // Connecting is the impolite side: it makes the opening offer and
      // keeps it when the two ends collide.
      const conn = new MeetPeerConnection(peerConfig(options), { polite: false, recoveryTimeout: options?.recoveryTimeout });
      await conn.connect(invitecode, options);
      return conn;
    },
  };
}
